import type { PathItem } from '@mimir/primitive/pathItem';
import type { FolderViewModel, FolderPathSegmentDto, FolderDto } from '@mimir/common/dto/folderModels';
import { FolderTypeName, FolderKind } from '@mimir/common/dto/folderModels';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import { getFolder, getFolderContent, getFolderAncestry } from '@mimir/web/api/globalClientFolder';
import { logErrorAndShowToast } from '../util/uiError';
import sortBy from 'lodash/sortBy';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { saveWorkFolderId } from './userSettings';
import { dinaMonthStrings } from '@mimir/common/util/date';
import { padDateNumber } from '@mimir/common/util/padDateNumber';
import { reactive, vueDelete, vueSet, computed, ref, readonly } from '../compat/vueCompat';
import type { AccessControlUpdatePayload } from '../dialogs/itemAccessControlDialogRequest';
import { callApiCommand } from '@mimir/client-common/api/callApiCommand';
import { GetFirstChildFoldersBatchCommand, GetFolderCommand } from '@mimir/common/api/folderCommands';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import type { MetadataErrors } from '@mimir/common/dto/mdfModels';
import { compareFoldersByLexicalOrder } from '@mimir/common/util/folderLexicalOrdering';
import { RootFolder } from '../util/path-to-root';
import { getGlobalStore } from '../store';
import { canCreateItemInFolder } from '@mimir/common/util/itemAccessControl';
import type { PreviewItemOps } from '../model/previewItemOps';

interface FolderState {
  rootId: string | null;
  tree: Record<string, FolderViewModel>;
  workFolder: FolderViewModel | null;
  currentFolder: FolderViewModel | null; // Last selected folder in left folder tree.
  openedFolders: string[];
  loadingSearch: boolean;
  loadingTree: boolean;

  paths: Record<string, FolderPathSegmentDto[]>;

  // true if there are unsaved changes in the folder metadata editor.
  hasMetadataUnsavedChanges: boolean;
  folderMetadataErrors: MetadataErrors;
}

export const folders: FolderState = reactive({
  rootId: null,
  tree: {}, // Also acts as a folder cache.
  currentFolder: null,
  workFolder: null,
  openedFolders: [],
  loadingSearch: false,
  loadingTree: false,
  paths: {},
  hasMetadataUnsavedChanges: false,
  folderMetadataErrors: {},
});


function createShortcut(label: string, folder: FolderViewModel) {
  const copy = cloneDeep(folder);
  copy.isShortcut = true;
  copy.name = label;
  delete copy.subTrees;
  copy.hasSubfolder = false;
  vueSet(folders.tree, `shortcut-${folder.id}`, copy);
  invariant(folders.rootId, 'Expected a root folder id');
  const rootFolder = folders.tree[folders.rootId];
  rootFolder.subTrees?.unshift(copy);
}


export function setCurrentFolder(payload: FolderViewModel | null) {
  folders.currentFolder = payload;
}


export function setOpenedFolders(payload: string[]) {
  folders.openedFolders = payload;
}

export function setHasFolderMetadataUnsavedChanges(payload: boolean) {
  folders.hasMetadataUnsavedChanges = payload;
}

export function setFolderMetadataErrors(payload: MetadataErrors) {
  folders.folderMetadataErrors = payload;
}

export function cacheFolderPath(folderId: string, path: FolderPathSegmentDto[]) {
  vueSet(folders.paths, folderId, path);
}


export function setWorkFolder(payload: FolderViewModel | null) {
  if (folders.workFolder) {
    addRecentFolder(folders.workFolder.id);
  }
  folders.workFolder = payload;
}


export function updateWorkFolder(newName: string) {
  if (folders.workFolder?.name) {
    folders.workFolder.name = newName;
  }

  const pathLength = folders.workFolder?.folderPaths?.length;
  if (folders.workFolder?.folderPaths && pathLength) {
    // The folder itself is always at the last index.
    vueSet(folders.workFolder.folderPaths[pathLength - 1], 'name', newName);
  }
}


export function invalidatePathCacheForFolder(folderId: string) {
  const idsToRemove: string[] = [folderId];
  for (const [id, paths] of Object.entries(folders.paths)) {
    const res = paths.find(p => p.id === folderId);
    if (res) {
      idsToRemove.push(id);
    }
  }
  for (const id of idsToRemove) {
    vueDelete(folders.paths, id);
  }
}


export function renameFolderInTree(id: string, newName: string) {
  const folder = folders.tree[id];
  if (folder) {
    folder.name = newName;
    // reordering the subfolders in the parent folder
    // to reflect the new name.
    const parentFolder = folders.tree[folder.parent!];
    if (parentFolder && parentFolder.subTrees) {
      parentFolder.subTrees = parentFolder.subTrees.sort(sortFolderBySortKeyOrName);
    }
  }
}

export function updateSortKeyInTree(id: string, newSortKey: string | null | undefined) {
  const folder = folders.tree[id];
  if (folder) {
    folder.sortKey = newSortKey;
    const parentFolder = folders.tree[folder.parent!];
    if (parentFolder && parentFolder.subTrees) {
      parentFolder.subTrees = parentFolder.subTrees.sort(sortFolderBySortKeyOrName);
    }
  }
}

export function updateFolderOwnerInTree(folderId: string, newOwner: string) {
  const folder = folders.tree[folderId];
  if (folder) {
    folder.owner = newOwner;
  }
}


function sortFolderBySortKeyOrName(a: FolderViewModel, b: FolderViewModel) {
  if (a.isShortcut || !a.name) {
    return -1;
  }
  if (b.isShortcut || !b.name) {
    return 1;
  }

  return compareFoldersByLexicalOrder(a, b, 'asc');
}

function setSubFolders(parentFolder: FolderViewModel, subFolderDtos: FolderDto[]) {
  const subFolders: FolderViewModel[] = [];
  for (const f of subFolderDtos) {
    const subFolder: FolderViewModel = Object.assign({}, {
      ...f,
      loading: false,
      hasLoadedSubFolders: false,
      subTrees: f.hasSubfolder ? [] : undefined,
    });
    subFolders.push(subFolder);
    vueSet(folders.tree, f.id, subFolder);
  }
  parentFolder.hasLoadedSubFolders = true;
  vueSet(parentFolder, 'subTrees', subFolders.sort(sortFolderBySortKeyOrName));
}


export function removeFolderFromTree(folderId: string) {
  const folder = folders.tree[folderId];

  // To separate if checks or typescript complains about index type when accessing map.
  if (!folder) {
    return;
  }

  if (!folder.parent) {
    return;
  }

  const parent = folders.tree[folder.parent];
  if (parent && parent.subTrees) {
    const index = parent.subTrees.indexOf(folder);
    if (index >= 0) {
      parent.subTrees.splice(index, 1);
      if (parent.subTrees.length === 0) {
        vueDelete(parent, 'subTrees'); // Make sure the expand icon disappears.
        parent.hasSubfolder = false;
      }
    }
  }

  vueDelete(folders.tree, folderId);
}


export function addFolderToParent(parentFolderId: string, newFolder: FolderDto) {
  const viewModel: FolderViewModel = Object.assign({}, {
    ...newFolder,
    subTrees: newFolder.hasSubfolder ? [] : undefined,
    loading: false,
    hasLoadedSubFolders: false,
    parentFolder: parentFolderId
  });

  // The parentFolder passed may come from search result. Those objects are not
  // cached in the tree, so we should fetch the correct object before updating.
  const treeFolderParent = folders.tree[parentFolderId];
  if (!treeFolderParent) {
    return; // A folder has been loaded that is not currently present in tree yet.
  }

  if (!treeFolderParent.subTrees) {
    vueSet(treeFolderParent, 'subTrees', []);
  }
  const unSorted = [...treeFolderParent.subTrees!];
  unSorted.push(viewModel);
  treeFolderParent.subTrees = unSorted.sort(sortFolderBySortKeyOrName);
  vueSet(folders.tree, viewModel.id, viewModel);
}


function resetTree() {
  folders.tree = {};
  folders.openedFolders = [];
}


export function updateFolderAccessControl(folderId: string, controls: AccessControlUpdatePayload) {
  const folder = folders.tree[folderId];
  if (folder) {
    if (controls.visibleTo) {
      vueSet(folder, 'visibleTo', controls.visibleTo);
    }
    if (controls.updatableBy) {
      vueSet(folder, 'updatableBy', controls.updatableBy);
    }
  }
}


export function getTree(rootId: string | null, tree: Record<string, FolderViewModel>) {
  invariant(rootId === folders.rootId, 'Wrong rootId');
  if (rootId === null) {
    return [];
  }
  return tree[rootId]?.subTrees ?? [];
}


export function getTreeIncludingRoot(rootId: string | null, tree: Record<string, FolderViewModel>) {
  invariant(rootId === folders.rootId, 'Wrong rootId');
  return rootId && tree[rootId] ? [tree[rootId]] : [];
}


export function getFolderFromCache(folderId: string): FolderViewModel | undefined {
  return folders.tree[folderId];
}


export const currentFolderPath = computed(() => getCurrentFolderPath(folders.rootId, folders.tree, folders.currentFolder, folders.paths));


function getCurrentFolderPath(
  rootId: string | null,
  tree: Record<string, FolderViewModel>,
  currentFolder: FolderViewModel | null,
  paths: Record<string, FolderPathSegmentDto[]>,
): PathItem[] {
  if (currentFolder === null ||
      currentFolder.id === rootId) {
    return [];
  }

  let parentFolderId = currentFolder.parent;
  let pathItems: PathItem[] = [{ name: currentFolder.name, folderId: currentFolder.id }];
  let foundPathToRoot = false;
  while (parentFolderId !== undefined) {

    if (parentFolderId === rootId) {
      foundPathToRoot = true;
      break;
    }

    const parentFolder = tree[parentFolderId];
    if (parentFolder) {
      pathItems.unshift({
        name: parentFolder.name,
        folderId: parentFolder.id,
      });
      parentFolderId = parentFolder.parent;
    } else {
      parentFolderId = undefined;
    }
  }

  // We did not find the path in the folder cache. Let's check the ancestry cache instead.
  // It's unlikely we don't find the path cached here, but if it does the default will be
  // that only the current folder with a navigation arrow to parent will show up.
  if (!foundPathToRoot) {
    const folderPath = paths[currentFolder.id];
    if (folderPath) {
      pathItems = [];
      for (const path of folderPath) {
        if (path.id === rootId) {
          continue;
        }
        pathItems.push({
          name: path.name,
          folderId: path.id
        });
      }
    }
  }

  return pathItems;
}


export function doExpandFolder(folderId: string, isFolderTreeActive: boolean) {
  if (isFolderTreeActive && !folders.openedFolders.includes(folderId)) {
    folders.openedFolders.push(folderId);
  }
}


async function updateWorkFolderPath(folderId: string) {
  try {
    const folderAncestry = await getFolderAncestry([folderId], 'folder');
    let folderPaths: FolderPathSegmentDto[] = [];

    if (folderAncestry[folderId]?.paths[folderId]) {
      folderPaths = folderAncestry[folderId].paths[folderId][0];
    }

    if (folders.workFolder?.folderPaths) {
      folders.workFolder.folderPaths = folderPaths;
    }
  } catch (err) {
    console.log('cannot update the work folder path: ', err);
  }
}


export async function fetchSubfolders(folder: FolderViewModel | string, signal: AbortSignal | undefined) {
  try {
    if (typeof folder === 'string') {
      const cachedFolder = folders.tree[folder];
      if (cachedFolder && !cachedFolder.hasLoadedSubFolders && cachedFolder.hasSubfolder) {
        const subFolders = await callApiCommand(globalBackendClient, new GetFirstChildFoldersBatchCommand({ folderId: cachedFolder.id }), { signal });
        setSubFolders(cachedFolder, subFolders);
      }
    } else if (folder.hasSubfolder) {
      const subFolders = await callApiCommand(globalBackendClient, new GetFirstChildFoldersBatchCommand({ folderId: folder.id }), { signal });
      setSubFolders(folder, subFolders);
    }
  } catch (err)  {
    logErrorAndShowToast('Error occurred while loading sub folders', err, 'ex in fetchSubfolders');
  }
}


export async function moveFolder(sourceId: string, destinationId: string, contentId: string) {
  if (!sourceId || !contentId || !destinationId) {
    console.debug(`missing content ${sourceId} ${destinationId} ${contentId}`);
    return;
  }

  const shouldUpdateWorkFolderPath = folders.workFolder?.folderPaths?.some(pathSegment => pathSegment.id === contentId);
  const currentWorkFolderId = folders.workFolder?.id;
  if (shouldUpdateWorkFolderPath && currentWorkFolderId) {
    updateWorkFolderPath(currentWorkFolderId).catch(err => console.error(err));
  }

  const folder = folders.tree[contentId];
  const sourceFolder = folders.tree[sourceId];
  const targetFolder = folders.tree[destinationId];

  if (!folder) {
    console.debug('No folder found in cache');
    // The folder was never loaded in the tree, so we can forget about movement logic.
    return;
  }

  if (sourceFolder && sourceFolder.subTrees) {
    const index = sourceFolder.subTrees.findIndex(node => node.id === folder.id);
    if (index >= 0) {
      sourceFolder.subTrees.splice(index, 1);
    }

    // Remove expansion arrow if the sub tree is empty.
    if (sourceFolder.subTrees.length === 0) {
      vueDelete(sourceFolder, 'subTrees');
      sourceFolder.hasSubfolder = false;
    }
  } else {
    console.debug('No sourceFolder found in cache', sourceFolder);
  }

  if (targetFolder) {
    if (targetFolder.hasSubfolder) {
      if (!targetFolder.hasLoadedSubFolders) {
        await fetchSubfolders(targetFolder, undefined);
      } else {
        targetFolder.subTrees = sortBy([...targetFolder.subTrees!, folder], (vm => vm.name));
      }
    } else {
      vueSet(targetFolder, 'subTrees', [folder]);
      targetFolder.hasSubfolder = true;
    }
  } else {
    console.debug('No targetFolder found in cache', targetFolder);
  }

  folder.parent = destinationId;
}


async function loadPathFromServer(folderId: string) {
  try {
    const paths = await getFolderAncestry([folderId], 'folder');
    const resolvedPath = paths[folderId];

    if (!resolvedPath) {
      return;
    }

    const flattenItemAncestors = Object.entries(resolvedPath.paths).reduce(
      (acc: FolderPathSegmentDto[][], [_, ancestor]) => acc.concat(ancestor),
      [],
    );

    cacheFolderPath(folderId, flattenItemAncestors[0]);
    return;
  } catch (err) {
    logErrorAndShowToast('Failed to load folder, please try again.', err, 'ex in loadPathFromServer');
  }
}


export async function resolveWorkFolder(folderId: string) {
  try {
    const workFolder = await getFolder(folderId);
    const viewModel: FolderViewModel = await getPathAndConstructFolderViewModel(workFolder);
    await loadPathFromServer(workFolder.id);
    setWorkFolder(viewModel);
  } catch (err: any) {
    if (err?.response?.status === 404) {
      showToast('Could not load work folder, it no longer exists.', TOAST.INFO);
    }
    saveWorkFolderId(null);
  }
}


async function loadDinaFolderDay(date: Date, dinaStoryFolder: FolderViewModel) {
  const year = date.getFullYear().toString();
  const month = dinaMonthStrings[date.getMonth()];
  const day = padDateNumber(date.getDate());
  console.log(`Loading bookmark ${year} / ${month} / ${day}`);
  const yearFolder = dinaStoryFolder.subTrees?.find(f => f.name === year);
  if (yearFolder && yearFolder.hasSubfolder) {
    if (!yearFolder.hasLoadedSubFolders) {
      await fetchSubfolders(yearFolder, undefined);
    }
    const monthFolder = yearFolder.subTrees?.find(f => f.name === month);
    if (monthFolder && monthFolder.hasSubfolder) {
      if (!monthFolder.hasLoadedSubFolders) {
        await fetchSubfolders(monthFolder, undefined);
      }
      return monthFolder.subTrees?.find(f => f.name === day);
    }
  }
}


async function createShortcuts() {
  if (!folders.rootId) {
    return;
  }
  const root = folders.tree[folders.rootId];
  if (!root || !root.subTrees) {
    return;
  }

  // There are multiple conventions of DiNA Stories folder name.
  // So, it is safest to do case-insensitive matching to find that folder.
  const dinaStories = root.subTrees.find(f => f.name.toLocaleLowerCase() === 'dina stories');
  if (!dinaStories) {
    return;
  }

  if (!dinaStories.hasLoadedSubFolders) {
    await fetchSubfolders(dinaStories, undefined);
  }
  if (dinaStories.hasSubfolder) {
    const today = new Date();
    let folder = await loadDinaFolderDay(today, dinaStories);
    if (!folder) {
      // Try one more time with yesterdays date as a fallback.
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      folder = await loadDinaFolderDay(yesterday, dinaStories);
    }
    if (folder) {
      createShortcut('Today\'s Dina Stories', folder);
    } else {
      console.log('Unable to load todays dina folder');
    }
  }
}

export async function initFolderTree(client: BackendClient, isFolderTreeActive: boolean, signal: AbortSignal) {
  try {
    resetTree();
    folders.loadingTree = true;

    const tenantRootDto = await callApiCommand(client, new GetFolderCommand({ folderId: FolderKind.TenantRoot }), { signal });
    const tenantRoot: FolderViewModel = {
      ...tenantRootDto,
      loading: false,
      hasLoadedSubFolders: false,
      subTrees: [],
      name: RootFolder.DisplayName,
    };
    folders.rootId = tenantRoot.id;
    vueSet(folders.tree, tenantRoot.id, tenantRoot);

    if (tenantRoot.hasSubfolder) {
      tenantRoot.loading = true;
      const subFolders = await getFolderContent(tenantRoot.id, {type: FolderTypeName}) as FolderDto[];
      setSubFolders(tenantRoot, subFolders);
      tenantRoot.loading = false;
      void createShortcuts();
    }

    // Attempt to expand currentFolder if present.
    if (folders.currentFolder !== null) {
      const subFolder = tenantRoot.subTrees?.find(node => node.id === folders.currentFolder!.id);
      if (subFolder) {
        if (subFolder.hasSubfolder && !subFolder.hasLoadedSubFolders) {
          subFolder.loading = true;
          await fetchSubfolders(subFolder, signal);
          subFolder.loading = false;
        }
        doExpandFolder(subFolder.id, isFolderTreeActive);
      }
    }

  } catch (err) {
    logErrorAndShowToast('Failed to initialize folder tree', err, 'ex in initFolderTree');
  } finally {
    folders.loadingTree = false;
  }
}


export interface LoadAndSetFolderParams {
  folderId: string;
  isFolderTreeActive: boolean;
  previewItemOps: PreviewItemOps | null;
  // True when the call is a refresh of the current search rather than a folder
  // navigation; passed through so the folder preview does not take over an item
  // the user is actively previewing.
  isRefreshSearch: boolean;
  // The value is derived from the presence of a `previewItemId` query param in the URL and folder metadata feature flag.
  showFolderInQuickPreview: boolean;
}

export async function loadAndSetFolder(params: LoadAndSetFolderParams) {
  const { folderId, isFolderTreeActive, previewItemOps, showFolderInQuickPreview, isRefreshSearch } = params;
  try {
    const folder = await getFolder(folderId);
    // Value of 'folderId' can be 'tenant-root', but 'state.tree' uses the resolved id for indexing
    // So use 'folder.id' instead.
    let folderInTree: FolderViewModel | undefined = folders.tree[folder.id];
    if (folderInTree) {

      // If the folder is in the tree, we'll update it updated relevant props.
      folderInTree.hasSubfolder = folder.hasSubfolder;
      folderInTree.name = folder.name;
      folderInTree.parent = folder.parent;
      folderInTree.metadata = folder.metadata;

      // We only fetch sub folders if the folder doesn't already have them, and not visible. This fixes an edge case
      // with path displaying, so that the path displayed can be cached and sound. This logic can be removed once
      // we have an endpoint to get ancestry path to the root.
      if (!isFolderTreeActive && folderInTree.hasSubfolder && !folderInTree.hasLoadedSubFolders) {
        vueDelete(folderInTree, 'subTrees');
        fetchSubfolders(folderInTree, undefined).catch(err => console.error(err));
      } else if (folderInTree.subTrees && !folderInTree.hasSubfolder) {
        // We had cached sub folders, but the folder no longer has any.
        vueDelete(folderInTree, 'subTrees');
      }
    } else {
      // In this scenario, the user has likely entered a folder directly via refreshing the app
      folderInTree = Object.assign({}, {
        ...folder,
        loading: false,
        hasLoadedSubFolders: false,
      });
      vueSet(folders.tree, folder.id, folderInTree);
      const promises = [fetchSubfolders(folderInTree, undefined), loadPathFromServer(folder.id)];
      await Promise.all(promises).catch(err => console.log(err));
    }

    setCurrentFolder(folderInTree);
    if (showFolderInQuickPreview) {
      previewItemOps?.tryPreviewFolder(folderInTree, isRefreshSearch);
    }
    return folderInTree;
  } catch(err) {
    logErrorAndShowToast('Failed to load folder', err, 'ex in loadAndSetFolder');
  }
}


export async function getUncachedPathStringifiedForFolder(folderId: string) {
  let paths: FolderPathSegmentDto[] | null = null;
  if (folders.paths[folderId]) {
    paths = folders.paths[folderId];
  } else {
    await loadPathFromServer(folderId);
    paths = folders.paths[folderId];
  }

  if (!paths) {
    return null;
  }
  let pathString = '';
  pathString = paths.map(p => p.name).reduce((prev, cur) => `${prev}/${cur}`, pathString);
  return pathString;
}


export async function setAsWorkFolder(payload: FolderViewModel | FolderDto | null | string) {
  // We probably received a message with an id through postMessage API.
  // We need to resolve the folder first.
  if (typeof payload === 'string') {
    try {
      const folderDto = await getFolder(payload);
      const folderViewModelObj = await getPathAndConstructFolderViewModel(folderDto);
      setWorkFolder(folderViewModelObj);
      saveWorkFolderId(folderDto.id);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // We don't really want to bother the user with toast messages. We can silently set it anyway,
        // and if the user navigates to a folder that does not exist, we have other UI components informing
        // the user accordingly, but let's log it to the console to be sure.
        console.warn('Could not find folder - 404.', err);

        // In the event we're dealing with a race condition- let's still save the workfolder ID.
        saveWorkFolderId(payload);
      } else {
        // Something unexpected happened, we will want to hear about it.
        logErrorAndShowToast('Something went wrong when resolving work folder', err);
      }
    }
  } else {
    const folderViewModelObj = payload === null ? null : await getPathAndConstructFolderViewModel(payload);
    setWorkFolder(folderViewModelObj);
    saveWorkFolderId(payload === null ? null : payload.id);
  }
}


export async function getPathAndConstructFolderViewModel(folderDto: FolderDto) {
  let folderPaths: FolderPathSegmentDto[] = [];
  const folderId = folderDto.id;

  const folderAncestry = await getFolderAncestry([folderId], 'folder');
  if (folderAncestry[folderId]?.paths[folderId]) {
    folderPaths = folderAncestry[folderId].paths[folderId][0];
  }

  const viewModel: FolderViewModel = {
    folderPaths,
    loading: false,
    hasLoadedSubFolders: false,
    ...folderDto,
  };
  return viewModel;
}

/**
 * Checks if the current user has permission to create items in the specified folder.
 * This check combines folder access controls with user permissions.
 *
 * @param folder - The folder to check permissions for
 * @returns true if the user can create items in the folder, false otherwise
 */
export const canUserCreateInFolder = (folder: FolderViewModel | FolderDto | null): boolean => {
  if (!folder) {
    return false;
  }

  const store = getGlobalStore();
  const currentUser = store?.getters?.user;
  if (!currentUser) {
    return true; // No user info, default to allowing
  }

  return canCreateItemInFolder(
    folder.updatableBy,
    folder.owner,
    folder.kind,
    currentUser
  );
};

export const initialTargetFolder = () => {
  if (folders.currentFolder) {
    return folders.currentFolder;
  }
  if (folders.workFolder) {
    return folders.workFolder;
  }

  return folders.rootId ? getFolderFromCache(folders.rootId) ?? null : null;
};

const openedFolders = ref<string[]>([]);
export const recentFolders = readonly(openedFolders);
let recentFoldersKey = '';

export function loadRecentFolders(userId: string) {
  recentFoldersKey = 'recentFolders-' + userId;
  const stored = localStorage.getItem(recentFoldersKey);
  if (stored) {
    openedFolders.value = JSON.parse(stored);
  }
}

export function addRecentFolder(folderId: string) {
  const existingIndex = openedFolders.value.indexOf(folderId);
  if (existingIndex !== -1) {
    openedFolders.value.splice(existingIndex, 1);
  }
  openedFolders.value.unshift(folderId);
  if (recentFoldersKey) {
    localStorage.setItem(recentFoldersKey, JSON.stringify(openedFolders.value.slice(0, 10)));
  }
}
